import type { Color } from "../../common/color.js";
import { withAlpha } from "../../common/color.js";
import { Container, type ContainerOptions } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { KeyEventArgs, MouseEventArgs, WheelEventArgs } from "../core/events.js";
import {
  clamp,
  rectContains,
  rectDeflate,
  type Insets,
  type Point,
  type Rect,
} from "../../common/geometry.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";
import { ScrollBar } from "./ScrollBar.js";
import { ScrollViewer, type ScrollViewerOptions } from "./ScrollViewer.js";
import {
  DEFAULT_FLIP_SHAPE,
  easeFlip,
  flipFrame,
  mirrorTransition,
  pickTransition,
  type FlipPart,
  type FlipShape,
  type FlipTransition,
  type FlipTransitionSpec,
} from "./flipTransitions.js";

/** Which face of a card is being looked at. */
export type FlipSide = "front" | "back";

/** What a gesture does to a card. */
export type FlipAction =
  /** Nothing. What every gesture the application has not asked for does. */
  | "none"
  /** Show whichever face is not showing. */
  | "toggle"
  | "front"
  | "back";

/** A gesture, with the transition and duration it wants for its own turn. */
export interface FlipGesture {
  action: FlipAction;
  /** Overrides the card's `transition` for turns this gesture starts. */
  transition?: FlipTransitionSpec;
  /** Overrides the card's `flipSeconds`. */
  seconds?: number;
}

/** A gesture, or just the action when the card's own transition will do. */
export type FlipGestureSpec = FlipAction | FlipGesture;

/**
 * What each pointer gesture over a card does.
 *
 * All five are wired; which of them do anything is the application's choice.
 * `hover` and `leave` are off by default deliberately — a card that turns
 * when the pointer arrives *and* turns when it is clicked gives two answers
 * to one gesture, and the pointer has to arrive before it can click.
 */
export interface FlipCardGestures {
  /** The pointer arrived over the card. */
  hover?: FlipGestureSpec;
  /** The pointer left it. */
  leave?: FlipGestureSpec;
  click?: FlipGestureSpec;
  /** The right button, which never raises the browser's own menu here. */
  contextClick?: FlipGestureSpec;
  /**
   * Note that a double click raises `click` first, twice. Setting both means
   * the card turns three times, which is almost never what was wanted.
   */
  doubleClick?: FlipGestureSpec;
}

export interface FlipCardFaceOptions extends ScrollViewerOptions {
  /** Fill behind the face's contents; null uses the theme's field color. */
  background?: Color | null;
}

/**
 * One side of a `FlipCard`: a scrolling, zooming viewport over anything.
 *
 * A plain `ScrollViewer` with the border off and a fill the card can set, so
 * the two faces and the card's own ground agree — which matters because a
 * dissolve blends a face *toward* that ground, and a mismatch there is a flash
 * of the wrong color in the middle of every fade.
 */
export class FlipCardFace extends ScrollViewer {
  background: Color | null;

  constructor(options: FlipCardFaceOptions = {}) {
    super({ showBorder: false, ...options });
    this.background = options.background ?? null;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    if (this.background) {
      rc.fillRect(this.localRect, this.background);
      return;
    }
    super.onPaint(rc, theme);
  }
}

export interface FlipCardOptions extends ContainerOptions {
  /** Which face the card starts on. */
  showing?: FlipSide;
  /** The turn a gesture gets when it does not name one of its own. */
  transition?: FlipTransitionSpec;
  /** Seconds one turn takes. Zero cuts straight to the other face. */
  flipSeconds?: number;
  /** Mirror the transition on the way back to the front. */
  reverseOnReturn?: boolean;
  /** Strips a turn's squash is approximated with. */
  strips?: number;
  /** Shutters a blind is made of. */
  shutters?: number;
  /** How far a face shrinks when it is edge-on, 0..1. */
  perspective?: number;
  /** How dark a face goes when it is edge-on, 0..1. */
  shade?: number;
  /** The card's ground, behind and between the faces. Null uses the theme. */
  background?: Color | null;
  /** Draw the theme's panel chrome around the card. */
  showBorder?: boolean;
  /** Space held between that chrome and the faces. */
  padding?: Insets;
  /** Hand the pointer to the controls on a face; see `interactiveFaces`. */
  interactiveFaces?: boolean;
  /** Let a mouse drag pan the showing face, as a finger already does. */
  dragToPan?: boolean;
  /** Whether the faces zoom at all, on ctrl+wheel or a pinch. */
  zoomEnabled?: boolean;
  gestures?: FlipCardGestures;
}

/** Seconds on a monotonic clock that runs whether or not frames are. */
const now = (): number => performance.now() / 1000;

const ZOOM_STEP = 1.1;

const other = (side: FlipSide): FlipSide => (side === "front" ? "back" : "front");

function asGesture(spec: FlipGestureSpec | undefined): FlipGesture | null {
  if (spec === undefined) return null;
  return typeof spec === "string" ? { action: spec } : spec;
}

/**
 * Two faces and a turn between them.
 *
 * The same ownership as `TabControl` and `Carousel` — the card creates and
 * sizes both faces, so an application fills `card.front` and `card.back` and
 * never positions either — but a card has exactly two of them and no chooser,
 * because the gesture *is* the chooser. Each face is a `FlipCardFace`, which
 * is a `ScrollViewer`, so either side scrolls and zooms over as much text and
 * as many pictures as it likes.
 *
 * ```ts
 * const card = new FlipCard({ transition: "flipX", flipSeconds: 0.45 });
 * card.front.add(new Label({ x: 16, y: 16, width: 260, text: "Hello" }));
 * card.back.add(new ImageControl({ x: 0, y: 0, width: 300, height: 400, source: url }));
 * card.gestures = { click: "toggle", contextClick: { action: "toggle", transition: "random" } };
 * ```
 *
 * **The card is one object to the pointer.** `hitTest` stops at the card
 * rather than descending, so hovering, clicking and right-clicking anywhere
 * over it reach the card — Webwin's events do not bubble, so without this a
 * click on a `Label` sitting on a face would be heard by the label and by
 * nobody else. The wheel, a pinch and a drag are forwarded to the showing
 * face, and the faces' scroll bars are the one thing let through, because a
 * scrolling face whose bar cannot be dragged is a promise the card breaks.
 * `interactiveFaces` turns the whole rule off for a card whose faces carry
 * controls of their own.
 */
export class FlipCard extends Container {
  readonly front: FlipCardFace;
  readonly back: FlipCardFace;

  /** The turn in flight: what it is leaving, how, and when it started. */
  #flip: {
    from: FlipSide;
    transition: FlipTransition;
    seconds: number;
    startedAt: number;
    /** How much of it a reversed turn had already done when it began. */
    start: number;
  } | null = null;
  #showing: FlipSide;
  #animating = false;
  #padding: Insets;
  #background: Color | null;

  transition: FlipTransitionSpec;
  flipSeconds: number;
  reverseOnReturn: boolean;
  strips: number;
  shutters: number;
  perspective: number;
  shade: number;
  showBorder: boolean;
  interactiveFaces: boolean;
  dragToPan: boolean;
  gestures: FlipCardGestures;

  constructor(options: FlipCardOptions = {}) {
    super({ width: 320, height: 400, tabStop: true, ...options });
    this.#showing = options.showing ?? "front";
    this.transition = options.transition ?? "flipX";
    this.flipSeconds = options.flipSeconds ?? 0.45;
    this.reverseOnReturn = options.reverseOnReturn ?? true;
    this.strips = options.strips ?? DEFAULT_FLIP_SHAPE.strips;
    this.shutters = options.shutters ?? DEFAULT_FLIP_SHAPE.shutters;
    this.perspective = options.perspective ?? DEFAULT_FLIP_SHAPE.perspective;
    this.shade = options.shade ?? DEFAULT_FLIP_SHAPE.shade;
    this.#background = options.background ?? null;
    this.showBorder = options.showBorder ?? true;
    this.#padding = options.padding ?? { left: 0, top: 0, right: 0, bottom: 0 };
    this.interactiveFaces = options.interactiveFaces ?? false;
    this.dragToPan = options.dragToPan ?? false;
    this.gestures = options.gestures ?? {
      click: "toggle",
      contextClick: { action: "toggle", transition: "flipY" },
    };

    const face = (): FlipCardFace =>
      new FlipCardFace({
        background: this.#background,
        zoomEnabled: options.zoomEnabled ?? true,
      });
    this.front = this.controls.add(face());
    this.back = this.controls.add(face());

    this.on("pointerenter", () => this.#run(this.gestures.hover));
    this.on("pointerleave", () => this.#run(this.gestures.leave));
    this.on("click", (e) => this.#onClick(e));
    this.on("contextmenu", () => this.#run(this.gestures.contextClick));
    this.on("doubleclick", () => this.#run(this.gestures.doubleClick));
    this.on("wheel", (e) => this.#onWheel(e));
    this.on("keydown", (e) => this.#onKeyDown(e));

    this.#syncFaces();
    this.performLayout();
  }

  // --- faces --------------------------------------------------------------

  /** The face the card is showing, or arriving at while it turns. */
  get showing(): FlipSide {
    return this.#showing;
  }
  set showing(side: FlipSide) {
    this.showSide(side);
  }

  /** The face `showing` names. What the wheel and a pinch reach. */
  get activeFace(): FlipCardFace {
    return this.faceFor(this.#showing);
  }

  faceFor(side: FlipSide): FlipCardFace {
    return side === "front" ? this.front : this.back;
  }

  /** The knobs a turn's shape is built from, as this card has them set. */
  get flipShape(): FlipShape {
    return {
      strips: this.strips,
      shutters: this.shutters,
      perspective: this.perspective,
      shade: this.shade,
    };
  }

  /**
   * Where the faces live: everything the card's own chrome and padding leave.
   *
   * Also the client rect, so a face laid out at (0, 0) lands here and the
   * coordinate mapping every hit test goes through needs no special case.
   */
  get contentRect(): Rect {
    const frame = this.showBorder ? 2 : 0;
    const p = this.#padding;
    return rectDeflate(this.localRect, {
      left: p.left + frame,
      top: p.top + frame,
      right: p.right + frame,
      bottom: p.bottom + frame,
    });
  }

  override get clientRect(): Rect {
    return this.contentRect;
  }

  /**
   * The card's ground: what shows between the faces, what a dissolve blends
   * through, and what both faces are filled with. One color rather than
   * three, because the seam between them is exactly what a transition puts on
   * screen.
   */
  get background(): Color | null {
    return this.#background;
  }
  set background(v: Color | null) {
    this.#background = v;
    this.front.background = v;
    this.back.background = v;
    this.invalidate();
  }

  get padding(): Insets {
    return this.#padding;
  }
  set padding(v: Insets) {
    this.#padding = v;
    this.layout();
    this.invalidate();
  }

  // --- turning ------------------------------------------------------------

  /** True while a turn is still running. */
  get flipping(): boolean {
    return this.#flip !== null && this.rawProgress < 1;
  }

  /** The transition currently running, or null when the card is at rest. */
  get runningTransition(): FlipTransition | null {
    return this.#flip?.transition ?? null;
  }

  /** How far the turn in flight has run, 0..1. One when nothing is moving. */
  get rawProgress(): number {
    const flip = this.#flip;
    if (!flip) return 1;
    const seconds = flip.seconds * (1 - flip.start);
    if (seconds <= 0) return 1;
    return clamp((now() - flip.startedAt) / seconds, 0, 1);
  }

  /** The eased progress the frame is drawn against. */
  get progress(): number {
    const flip = this.#flip;
    if (!flip) return 1;
    return easeFlip(this.rawProgress, flip.start);
  }

  /** Show the other face. */
  flip(transition?: FlipTransitionSpec, seconds?: number): void {
    this.showSide(other(this.#showing), transition, seconds);
  }

  showFront(transition?: FlipTransitionSpec, seconds?: number): void {
    this.showSide("front", transition, seconds);
  }

  showBack(transition?: FlipTransitionSpec, seconds?: number): void {
    this.showSide("back", transition, seconds);
  }

  /**
   * Turn to `side`.
   *
   * A turn already heading there is left alone; one heading the other way is
   * *reversed* rather than restarted — the mirrored transition, picked up at
   * what is left of the one in flight — so a pointer that arrives and leaves
   * again before the card has settled retraces its steps instead of jumping.
   */
  showSide(side: FlipSide, transition?: FlipTransitionSpec, seconds?: number): void {
    if (side === this.#showing) return;
    const previous = this.#flip;
    const running = previous && previous.from === side ? this.rawProgress : null;

    let resolved: FlipTransition;
    let start = 0;
    if (previous && running !== null && running < 1) {
      resolved = mirrorTransition(previous.transition);
      start = clamp(1 - running, 0, 0.99);
    } else {
      resolved = pickTransition(transition ?? this.transition);
      // Going out and coming back are the same gesture seen twice; a slide
      // that left to the left should come home from the left.
      if (this.reverseOnReturn && side === "front") resolved = mirrorTransition(resolved);
    }

    const from = this.#showing;
    const length = seconds ?? this.flipSeconds;
    this.#showing = side;
    this.emit("flipping");

    if (length <= 0 || resolved === "none" || !this.desktop) {
      // Headless, or asked for a cut: there is no clock to run against and
      // nothing to run it for.
      this.#flip = null;
      this.#syncFaces();
      this.#syncAnimation();
      this.invalidate();
      this.emit("flipped");
      return;
    }

    this.#flip = { from, transition: resolved, seconds: length, startedAt: now(), start };
    this.#syncFaces();
    this.#syncAnimation();
    this.invalidate();
  }

  /**
   * Both faces are visible while one is turning into the other, and only the
   * showing one is otherwise.
   *
   * Visibility rather than paint order, because it is what everything *else*
   * reads: `effectivelyVisible` is what stops the hidden face taking a hit,
   * and it is how an application — or a designer drawing this tree — knows
   * which contents are on screen.
   */
  #syncFaces(): void {
    const turning = this.#flip !== null;
    this.front.visible = turning || this.#showing === "front";
    this.back.visible = turning || this.#showing === "back";
  }

  /** End the turn once its time has run out. Called from the paint pass. */
  #advance(): void {
    if (!this.#flip || this.rawProgress < 1) return;
    this.#flip = null;
    this.#syncFaces();
    this.#syncAnimation();
    this.emit("flipped");
  }

  /** A frame every tick, but only while something is actually moving. */
  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.#flip !== null && this.effectivelyVisible;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  // --- gestures -----------------------------------------------------------

  /** Turn to the back on the way in and back to the front on the way out. */
  flipOnHover(transition?: FlipTransitionSpec): void {
    this.gestures = {
      ...this.gestures,
      hover: { action: "back", ...(transition ? { transition } : {}) },
      leave: { action: "front", ...(transition ? { transition } : {}) },
    };
  }

  #run(spec: FlipGestureSpec | undefined): void {
    const gesture = asGesture(spec);
    if (!gesture || gesture.action === "none") return;
    if (!this.effectivelyEnabled || this.inputBlocked) return;
    const side = gesture.action === "toggle" ? other(this.#showing) : gesture.action;
    this.showSide(side, gesture.transition, gesture.seconds);
  }

  #onClick(e: MouseEventArgs): void {
    // The right button arrives as `contextmenu`, never as a click; anything
    // that is not the left one is somebody else's gesture.
    if (e.button !== 0) return;
    this.focus();
    this.#run(this.gestures.click);
  }

  #onKeyDown(e: KeyEventArgs): void {
    if (!this.focused || this.inputBlocked || e.ctrlKey || e.altKey || e.metaKey) return;
    switch (e.key) {
      case " ":
      case "Enter":
        this.flip();
        break;
      case "ArrowLeft":
        this.showFront();
        break;
      case "ArrowRight":
        this.showBack();
        break;
      default:
        return;
    }
    e.handled = true;
  }

  // --- input over the faces -----------------------------------------------

  /**
   * The card takes the point, so hover and both clicks reach it.
   *
   * Two exceptions. `interactiveFaces` hands everything back to whatever is
   * on the face, for a card whose sides carry controls rather than content.
   * And a scroll bar is always let through: forwarding the wheel and a drag
   * is enough to scroll a face, but a bar nobody can grab is worse than no
   * bar at all.
   */
  override hitTest(p: Point): Control | null {
    if (!this.visible) return null;
    if (!rectContains(this.localRect, p)) return null;
    const deep = super.hitTest(p);
    if (!deep) return null;
    if (this.interactiveFaces) return deep;
    for (let c: Control | null = deep; c && c !== this; c = c.parent) {
      if (c instanceof ScrollBar) return deep;
    }
    return this;
  }

  /** A point in the card's space, in the showing face's. */
  #toFace(p: Point): Point {
    const content = this.contentRect;
    return { x: p.x - content.x, y: p.y - content.y };
  }

  #onWheel(e: WheelEventArgs): void {
    // In interactive mode the face was the target and has already had its
    // turn; this is the wheel bubbling past on its way to an outer scroller.
    if (this.interactiveFaces || this.flipping) return;
    const face = this.activeFace;
    if (e.ctrlKey) {
      if (!face.zoomEnabled) return;
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      const before = face.zoom;
      face.setZoom(before * factor, this.#toFace({ x: e.x, y: e.y }));
      if (face.zoom !== before) e.handled = true;
      return;
    }
    // Shift picks the other axis, as it does everywhere on Windows.
    const used = face.panBy(e.shiftKey ? e.deltaY : e.deltaX, e.shiftKey ? 0 : e.deltaY);
    if (used.x !== 0 || used.y !== 0) e.handled = true;
  }

  /** A finger, or a mouse when `dragToPan`, scrolls the showing face. */
  override panBy(dx: number, dy: number): Point {
    if (this.interactiveFaces || this.flipping) return { x: 0, y: 0 };
    return this.activeFace.panBy(dx, dy);
  }

  override get pansWithMouse(): boolean {
    return this.dragToPan && !this.interactiveFaces;
  }

  override zoomBy(factor: number, at: Point): boolean {
    if (this.interactiveFaces || this.flipping) return false;
    return this.activeFace.zoomBy(factor, this.#toFace(at));
  }

  // --- layout -------------------------------------------------------------

  /** The faces are the card's to place; nothing else lives here. */
  protected override applyAnchors(): void {}

  protected override performLayout(): void {
    if (!this.front) return; // reached from Container's constructor
    const content = this.clientRect;
    this.front.setBounds(0, 0, content.width, content.height);
    this.back.setBounds(0, 0, content.width, content.height);
  }

  // --- painting -----------------------------------------------------------

  /** The ground the card shows between the faces, and dissolves through. */
  #ground(theme: Theme): Color {
    return this.#background ?? theme.palette.fieldBg;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    // An animator keeps the frames coming but runs nothing of its own, so the
    // turn is retired here, before the faces are asked where they are.
    this.#advance();
    if (this.showBorder) theme.drawPanel(rc, this.localRect, false);
    else rc.fillRect(this.localRect, this.#ground(theme));
  }

  /**
   * One face at rest; a list of pieces of both while the card turns.
   *
   * `Container.paintChildren` is replaced rather than extended because the
   * two faces are not laid out where they are drawn during a turn — the whole
   * of a transition is the difference between the two.
   */
  protected override paintChildren(rc: RenderContext, theme: Theme): void {
    const content = this.contentRect;
    if (content.width <= 0 || content.height <= 0) return;
    const flip = this.#flip;

    rc.clipped(content, () => {
      if (!flip) {
        rc.pushTransform(content.x, content.y, 1);
        this.activeFace.paint(rc, theme);
        rc.popTransform();
        return;
      }
      const size = { width: content.width, height: content.height };
      const arriving = other(flip.from);
      for (const p of flipFrame(flip.transition, this.progress, size, this.flipShape)) {
        this.#paintPart(rc, theme, p.face === "from" ? flip.from : arriving, p, content);
      }
    });
  }

  #paintPart(
    rc: RenderContext,
    theme: Theme,
    side: FlipSide,
    part: FlipPart,
    content: Rect,
  ): void {
    const window: Rect = {
      x: content.x + part.clip.x,
      y: content.y + part.clip.y,
      width: part.clip.width,
      height: part.clip.height,
    };
    if (window.width <= 0.5 || window.height <= 0.5) return;

    rc.clipped(window, () => {
      // The scale is about the center of the content area, so the face's own
      // origin moves by whatever that leaves it: cx - cx * scale.
      const cx = content.width / 2;
      const cy = content.height / 2;
      rc.pushTransform(
        content.x + cx * (1 - part.scale) + part.dx,
        content.y + cy * (1 - part.scale) + part.dy,
        part.scale,
      );
      this.faceFor(side).paint(rc, theme);
      rc.popTransform();

      // Both scrims are painted after the transform is popped, so they cover
      // the window rather than the face — a face pulled back by the
      // perspective must not take its own shading with it.
      if (part.veil > 0) rc.fillRect(window, withAlpha(this.#ground(theme), part.veil));
      if (part.shade > 0) rc.fillRect(window, { r: 0, g: 0, b: 0, a: part.shade });
    });
  }
}
